import type { ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { usePortal } from '../PortalContext';
import { PortalBrandHeader } from './PortalBrandHeader';

/**
 * Single-column chrome for every signed-in portal page: brand bar in the
 * tenant colour (the one inline-colour exception, as on EstimateSign), header
 * with logo/name, the customer's first name and Sign out, then a footer with
 * the company's contact details and policy links. No sidebar, no bell.
 */
export function PortalShell({ children }: { children: ReactNode }) {
  const { slug, company, me, primaryColor } = usePortal();
  const { signOut } = useAuth();
  const displayName = company.trading_name || company.name;
  const firstName = (me.customer_name || '').trim().split(/\s+/)[0] || 'there';

  return (
    <div className="min-h-screen bg-muted flex flex-col">
      <div className="h-1.5 w-full" style={{ backgroundColor: primaryColor }} />
      <header className="bg-background border-b">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4 px-4 py-3">
          <PortalBrandHeader
            name={displayName}
            logoUrl={company.logo_url}
            primaryColor={primaryColor}
            to={`/portal/${slug}/home`}
            className="min-w-0 shrink"
          />
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">Hi {firstName}</span>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => void signOut({ redirectTo: `/portal/${slug}` })}
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-6">
        {children}
      </main>

      <footer className="mx-auto w-full max-w-2xl px-4 pb-8 pt-4 text-center text-sm text-muted-foreground">
        <p className="font-medium text-foreground">{displayName}</p>
        {company.phone && <p><a href={`tel:${company.phone}`} className="hover:underline">{company.phone}</a></p>}
        {company.email && <p><a href={`mailto:${company.email}`} className="hover:underline">{company.email}</a></p>}
        {(company.terms_and_conditions_url || company.privacy_policy_url) && (
          <p className="mt-2 flex justify-center gap-4 text-xs">
            {company.terms_and_conditions_url && (
              <a href={company.terms_and_conditions_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                Terms &amp; Conditions
              </a>
            )}
            {company.privacy_policy_url && (
              <a href={company.privacy_policy_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                Privacy Policy
              </a>
            )}
          </p>
        )}
      </footer>
    </div>
  );
}
